import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/** How long a word takes to leave (its last letter included) before the next one arrives. */
const LEAVE_MS = 420;
const LETTER_DELAY_MS = 22;

/** One word, letter by letter: each in the sweep, each arriving (or leaving) a little after the one before. */
// The arriving word is the label's text itself (read once, as it is); the leaving one is only a picture of it.
/** The sweep's colours: the primary (the agent at work), or silver (the home composer's example prompts). */
const TONES = {
  // The moving band is a sheen: darker than the colour on a light ground, lighter on a dark one.
  primary:
    "bg-[linear-gradient(90deg,rgb(var(--th-purple-green))_0%,color-mix(in_srgb,rgb(var(--th-purple-green))_55%,black)_50%,rgb(var(--th-purple-green))_100%)] dark:bg-[linear-gradient(90deg,rgb(var(--th-purple-green))_0%,color-mix(in_srgb,rgb(var(--th-purple-green))_45%,white)_50%,rgb(var(--th-purple-green))_100%)]",
  silver: "[--silver:#8a9099] [--silver-hi:#dde1e6] dark:[--silver:#9aa1aa] dark:[--silver-hi:#f2f4f6] bg-[linear-gradient(90deg,var(--silver)_0%,var(--silver-hi)_50%,var(--silver)_100%)]",
};
type Tone = keyof typeof TONES;

const Letters = ({ text, leaving, tone }: { text: string; leaving: boolean; tone: Tone }) => (
  <span {...(leaving ? { "aria-hidden": true } : {})} className={leaving ? "absolute left-0 top-0 whitespace-nowrap" : "whitespace-nowrap"}>
    {Array.from(text).map((char, index) => (
      <span
        key={index}
        className={`inline-block ${TONES[tone]} bg-[length:240px_100%] bg-clip-text text-transparent [-webkit-text-fill-color:transparent]`}
        style={
          {
            "--i": index,
            whiteSpace: "pre",
            animation: `agent-wait-sweep 1.5s linear infinite, ${leaving ? "agent-wait-out" : "agent-wait-in"} ${leaving ? 380 : 600}ms cubic-bezier(0.16, 1, 0.3, 1) ${
              index * LETTER_DELAY_MS
            }ms both`,
          } as React.CSSProperties
        }
      >
        {char}
      </span>
    ))}
  </span>
);

/**
 * The agent's waiting words (OpenArt's waiting label, in the primary colour (`purpleGreen`: the accent violet #6266F0, lime in dark — the canvas's progress bar)):
 * a lighter violet sweeps across them letter by letter; a new word comes in out of a blur, letter after letter, as the old one
 * lifts away. The words are read once, as text: the arriving letters are the label itself, the leaving ones hidden.
 */
const AgentWaitingText = ({ text, tone = "primary", weight = "font-semibold" }: { text: string; tone?: Tone; weight?: "font-semibold" | "font-normal" }) => {
  const [leaving, setLeaving] = useState<string | null>(null);
  const shown = useRef(text);
  useEffect(() => {
    if (shown.current === text) return;
    setLeaving(shown.current);
    shown.current = text;
    const timer = setTimeout(() => setLeaving(null), LEAVE_MS + shown.current.length * LETTER_DELAY_MS);

    return () => clearTimeout(timer);
  }, [text]);

  // A long word (a search's whole query) is cut at the line's width, its end fading out, never under the timer.
  const box = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const node = box.current;
    if (node) setOverflows(node.scrollWidth > node.clientWidth + 1);
  }, [text]);

  return (
    <span
      ref={box}
      className={`relative inline-flex min-w-0 max-w-full overflow-hidden ${
        overflows ? "[mask-image:linear-gradient(90deg,black_calc(100%_-_28px),transparent)]" : ""
      } ${weight} motion-reduce:[&_*]:!animate-none`}
    >
      {leaving !== null && <Letters key={`out:${leaving}`} text={leaving} leaving tone={tone} />}
      <Letters key={`in:${text}`} text={text} leaving={false} tone={tone} />
    </span>
  );
};

export default AgentWaitingText;
